import React, { useState } from 'react';

import { PAGE_SIZE_SM } from '~constants/common';

import { getErrorMessage } from '~libs/axiosWrapper';

import { Button, ClickableIcon, ErrorResponse, Modal } from '~components';
import { SearchBar } from '~components/form-fields/search';
import { faMagnifyingGlass } from '~components/icon';
import { Row, TableView } from '~components/table-view';

import printToSignRecordsColumns from '~support-at-home/forms/budgets/printToSignRecordsList';
import printToSignRecordsSearchFields from '~support-at-home/forms/budgets/printToSignRecordsSearch';

import { useGetBudgetPrintToSignRecordsQuery } from '~support-at-home/actions/budgetPrintToSignRecords';

interface ComponentProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Print to Sign viewer (TAV-3313): lists the stored copy of every budget PDF printed for signing, with
 * who printed it and when, and lets each one be downloaded.
 */
const PrintToSignRecordsModal: React.FC<ComponentProps> = ({ isOpen, onClose }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchBarOpen, setSearchBarOpen] = useState(true);
  const [searchParams, setSearchParams] = useState<Record<string, unknown>>({});

  const handleClose = () => {
    setCurrentPage(1);
    setSearchParams({});
    onClose();
  };

  const handleSearch = (values: Record<string, unknown>) => {
    setSearchParams(values);
    setCurrentPage(1);
  };

  // Refetched on every open: a copy is stored when its Print to Sign job completes, which can be after
  // the viewer was last opened.
  const { data, isLoading, isFetching, error } = useGetBudgetPrintToSignRecordsQuery(
    { ...searchParams, limit: PAGE_SIZE_SM, offset: (currentPage - 1) * PAGE_SIZE_SM },
    { skip: !isOpen, refetchOnMountOrArgChange: true },
  );

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={(open) => {
        if (!open) handleClose();
      }}
      header="Print To Sign History"
      size="xl"
      footer={
        <Button color="secondary" onClick={handleClose}>
          Close
        </Button>
      }
    >
      <div>
        <div className="d-flex justify-content-end mb-2">
          <ClickableIcon
            icon={faMagnifyingGlass}
            id="budget-print-to-sign-records-search-toggle"
            onClick={() => setSearchBarOpen(!searchBarOpen)}
            toolTip="Toggle Search Bar"
            ariaLabel="Toggle search"
            ariaExpanded={searchBarOpen}
          />
        </div>
        <SearchBar
          id="budget-print-to-sign-records-searchbar"
          isOpen={searchBarOpen}
          fields={printToSignRecordsSearchFields}
          onSubmit={handleSearch}
          storageKey="budget-print-to-sign-records"
        />
        <ErrorResponse message={error ? getErrorMessage(error) : undefined} />
        <TableView
          id="budget-print-to-sign-records-table"
          data={(data?.rows ?? []) as Row[]}
          columns={printToSignRecordsColumns}
          isLoading={isLoading || isFetching}
          emptyMessage="No budgets have been printed to sign"
          allowPagination
          paginationType="pages"
          showCount
          currentPage={currentPage}
          pageSize={PAGE_SIZE_SM}
          totalCount={data?.total ?? 0}
          onPageChange={setCurrentPage}
          readonly
          striped
        />
      </div>
    </Modal>
  );
};

export default PrintToSignRecordsModal;
